import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { DriverCardData } from "@app/shared/components/driver-card/driver-card.component";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

export interface AssignDriverResult {
  team: any;
  boardId: string | null;
  boardName: string | null;
  billToClientId: string | null;
  billToClientName: string | null;
  isUpdate: boolean;
}

@Component({
  selector: "app-assign-driver-modal",
  templateUrl: "./assign-driver-modal.component.html",
  styleUrls: ["./assign-driver-modal.component.css"],
})
export class AssignDriverModalComponent implements OnChanges, OnDestroy {
  /** Whether the modal is visible. Use [(visible)] for two-way binding. */
  @Input() visible = false;
  /** The driver's actual ID (driverId on SchedulingTeamDriver). */
  @Input() driverId: string | null = null;
  /**
   * If the driver is already assigned to a team, supply its ID.
   * When omitted the component auto-detects by searching all team driver lists.
   */
  @Input() existingTeamId: string | null = null;
  /** Pre-selected board ID (for edit mode). */
  @Input() existingBoardId: string | null = null;
  /** Pre-selected board NAME (for edit mode - used when only name is known). */
  @Input() existingBoardName: string | null = null;
  /** Pre-selected bill-to client ID (for edit mode). */
  @Input() existingBillToId: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() assignSuccess = new EventEmitter<AssignDriverResult>();
  @Output() updateSuccess = new EventEmitter<AssignDriverResult>();
  @Output() unassignSuccess = new EventEmitter<void>();
  @Output() unassignWithNote = new EventEmitter<{ driverId: string; teamId: string }>();

  // ── Loading ────────────────────────────────────────────────────────────────
  isLoadingDriverDetail = false;
  isLoadingTeams = false;
  isSubmitting = false;

  // ── Data ──────────────────────────────────────────────────────────────────
  driverDetail: DriverCardData | null = null;
  schedulingTeams: any[] = [];
  billToClients: any[] = [];
  trailers: any[] = [];

  // ── Selections ─────────────────────────────────────────────────────────────
  selectedTeamId: string | null = null;
  selectedBoardId: string | null = null;
  selectedBillToId: string | null = null;
  selectedTrailerId: string | null = null;
  notifyOnAssign = false;

  // Auto-detected existing team (when existingTeamId is not supplied)
  private detectedExistingTeamId: string | null = null;

  // ── Email compose (survives main modal close) ──────────────────────────────
  showEmailCompose = false;
  emailBcc = "";
  emailSubject = "";
  emailBody = "";
  isSendingEmail = false;

  // ── Training notes ─────────────────────────────────────────────────────────
  showTrainingNotes = false;
  trainingNotesContent: string | null = null;
  trainingNotesClientName: string | null = null;

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  constructor(private http: HttpClient, private toastr: ToastrService, private featureFlags: FeatureFlagsService) {}

  private cancelLoad$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.cancelLoad$.next();
    this.cancelLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["visible"]) {
      if (this.visible && this.driverId) {
        this.initModal();
      } else if (!this.visible) {
        this.resetMainState();
      }
    }
  }

  private initModal(): void {
    // Cancel any in-flight requests from a previous open
    this.cancelLoad$.next();

    this.driverDetail = null;
    this.schedulingTeams = [];
    this.billToClients = [];
    this.selectedTeamId = null;
    this.selectedBoardId = null;
    this.selectedBillToId = null;
    this.selectedTrailerId = null;
    this.detectedExistingTeamId = null;
    this.notifyOnAssign = false;
    this.isSubmitting = false;

    // Load driver detail
    this.isLoadingDriverDetail = true;
    this.http.get(`api/board/driverDetail/${this.driverId}`).pipe(takeUntil(this.cancelLoad$)).subscribe({
      next: (d: any) => {
        try {
          this.driverDetail = d as DriverCardData;
          this.selectedTrailerId = (d as any).trailerId ?? null;
        } finally {
          this.isLoadingDriverDetail = false;
        }
      },
      error: () => {
        this.isLoadingDriverDetail = false;
      },
    });

    // Load trailers
    this.http.get("api/trailer/listDropDown").pipe(takeUntil(this.cancelLoad$)).subscribe({
      next: (data: any) => {
        this.trailers = Array.isArray(data) ? data.filter((t: any) => t.trailerNo != null) : [];
      },
      error: () => {},
    });

    // Load scheduling teams
    this.isLoadingTeams = true;
    this.http.get("api/board/listAll").pipe(takeUntil(this.cancelLoad$)).subscribe({
      next: (teams: any) => {
        try {
          this.schedulingTeams = Array.isArray(teams) ? teams : [];
          this.prefillSelections();
        } catch (e) {
          console.error("Error processing scheduling teams", e);
        } finally {
          this.isLoadingTeams = false;
        }
      },
      error: () => {
        this.isLoadingTeams = false;
        this.toastr.error("Failed to load scheduling teams");
      },
    });

    // Load bill-to clients
    this.http.get("api/board/clients").pipe(takeUntil(this.cancelLoad$)).subscribe({
      next: (c: any) => {
        this.billToClients = Array.isArray(c) ? c : [];
      },
      error: () => {},
    });
  }

  private prefillSelections(): void {
    if (this.existingTeamId) {
      // Parent knows the existing team (boards/dispatch edit mode)
      this.selectedTeamId = this.existingTeamId;
      if (this.existingBoardId) {
        this.selectedBoardId = this.existingBoardId;
      } else if (this.existingBoardName) {
        const team = this.schedulingTeams.find((t: any) => t.id === this.existingTeamId);
        const board = (team?.boards || []).find((b: any) => b.name === this.existingBoardName);
        this.selectedBoardId = board?.id || null;
      }
      this.selectedBillToId = this.existingBillToId;
    } else {
      // Auto-detect: search all teams for this driver (driver-detail mode)
      const existingTeam = this.schedulingTeams.find((t: any) =>
        (t.drivers || []).some((d: any) => d.driverId === this.driverId)
      );
      if (existingTeam) {
        const driverEntry = existingTeam.drivers.find((d: any) => d.driverId === this.driverId);
        this.detectedExistingTeamId = existingTeam.id;
        this.selectedTeamId = existingTeam.id;
        const board = (existingTeam.boards || []).find(
          (b: any) => b.name === driverEntry?.boardName
        );
        this.selectedBoardId = board?.id || driverEntry?.boardId || null;
        this.selectedBillToId = driverEntry?.billToClientId || null;
      }
    }
  }

  private resetMainState(): void {
    // Deliberately does NOT reset email compose or training notes -
    // those dialogs are shown after the main modal has already closed.
    this.driverDetail = null;
    this.schedulingTeams = [];
    this.billToClients = [];
    this.trailers = [];
    this.selectedTeamId = null;
    this.selectedBoardId = null;
    this.selectedBillToId = null;
    this.selectedTrailerId = null;
    this.notifyOnAssign = false;
    this.isSubmitting = false;
    this.isLoadingDriverDetail = false;
    this.isLoadingTeams = false;
    this.detectedExistingTeamId = null;
    // Cancel any in-flight load requests
    this.cancelLoad$.next();
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get effectiveExistingTeamId(): string | null {
    return this.existingTeamId || this.detectedExistingTeamId;
  }

  get isEditMode(): boolean {
    return !!this.effectiveExistingTeamId;
  }

  get selectedTeam(): any {
    return this.schedulingTeams.find((t: any) => t.id === this.selectedTeamId);
  }

  get availableBoards(): any[] {
    return this.selectedTeam?.boards || [];
  }

  get availableBillToClients(): any[] {
    if (!this.selectedTeam) return [];
    return this.billToClients.filter((c: any) => c.clientId === this.selectedTeam.clientId);
  }

  get modalHeader(): string {
    return this.isEditMode ? "Edit Driver Assignment" : "Assign Driver to Scheduling Team";
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  onTeamChange(): void {
    this.selectedBoardId = null;
    this.selectedBillToId = null;
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  confirm(): void {
    if (!this.selectedTeamId || !this.driverId || this.isSubmitting) return;
    this.isSubmitting = true;

    // Capture state before the async call completes (close() may clear fields)
    const isUpdate = this.effectiveExistingTeamId === this.selectedTeamId;
    const team = this.selectedTeam;
    const boardName = this.availableBoards.find((b: any) => b.id === this.selectedBoardId)?.name ?? null;
    const billToClient = this.availableBillToClients.find((c: any) => c.id === this.selectedBillToId);
    const capturedBoardId = this.selectedBoardId;
    const capturedBillToId = this.selectedBillToId;
    const capturedNotify = this.notifyOnAssign;
    const capturedTrailerId = this.selectedTrailerId;

    const assignmentObs = isUpdate
      ? this.http.patch("api/board/updateDriverAssignment", {
          schedulingTeamId: this.selectedTeamId,
          driverId: this.driverId,
          boardId: capturedBoardId,
          billToClientId: capturedBillToId,
        })
      : this.http.post("api/board/addDrivers", {
          schedulingTeamId: this.selectedTeamId,
          driverAssignments: [
            {
              driverId: this.driverId,
              boardName,
              billToClientId: capturedBillToId,
            },
          ],
        });

    assignmentObs.subscribe({
      next: () => {
        // If in edit mode, also update the trailer assignment
        const equipmentUpdate$ = isUpdate
          ? this.http.post("api/dispatch/assign", {
              driverIds: [this.driverId],
              tractorIds: [],
              trailerIds: capturedTrailerId ? [capturedTrailerId] : [],
              dryRun: false,
            })
          : null;

        const finish = () => {
          this.isSubmitting = false;
          this.toastr.success(isUpdate ? "Driver assignment updated" : "Driver assigned successfully");

          const result: AssignDriverResult = {
            team,
            boardId: capturedBoardId,
            boardName,
            billToClientId: capturedBillToId,
            billToClientName: billToClient?.companyName || null,
            isUpdate,
          };

          this.close();

          if (isUpdate) {
            this.updateSuccess.emit(result);
          } else {
            this.assignSuccess.emit(result);
            this.maybeShowTrainingNotes(team);
          }

          if (capturedNotify) {
            this.prepareEmailCompose(team, boardName);
          }
        };

        if (equipmentUpdate$) {
          equipmentUpdate$.subscribe({
            next: finish,
            error: () => {
              // Assignment succeeded; only equipment update failed - still consider success but warn
              this.toastr.warning("Assignment updated, but failed to update trailer");
              finish();
            },
          });
        } else {
          finish();
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.toastr.error(err || "Error saving assignment");
      },
    });
  }

  unassign(): void {
    if (!this.driverId || !this.effectiveExistingTeamId || this.isSubmitting) return;
    const driverId = this.driverId;
    const teamId = this.effectiveExistingTeamId;
    this.close();
    this.unassignWithNote.emit({ driverId, teamId });
  }

  // ── Training notes ─────────────────────────────────────────────────────────

  private maybeShowTrainingNotes(team: any): void {
    if (!team?.clientCustomerTraining) return;
    this.trainingNotesContent = team.clientCustomerTraining;
    this.trainingNotesClientName = team.clientName || null;
    this.showTrainingNotes = true;
  }

  dismissTrainingNotes(): void {
    this.showTrainingNotes = false;
    this.trainingNotesContent = null;
    this.trainingNotesClientName = null;
  }

  // ── Email compose ──────────────────────────────────────────────────────────

  private prepareEmailCompose(team: any, _boardName: string | null): void {
    const contacts = (team?.contacts || []).filter((c: any) => !c.doNotContact && c.email);
    const bccAddresses: string[] = contacts.map((c: any) => c.email);
    if ((this.driverDetail as any)?.email) bccAddresses.push((this.driverDetail as any).email);
    this.emailBcc = bccAddresses.join("; ");

    const driverName = this.driverDetail
      ? `${this.driverDetail.name} ${this.driverDetail.lastName}`
      : "Driver";
    this.emailSubject = `Driver Assignment \u2013 ${driverName}`;
    this.emailBody = `${driverName} has been assigned to ${team?.name || "a scheduling group"}.`;
    this.showEmailCompose = true;
  }

  sendEmail(): void {
    if (this.isSendingEmail) return;
    this.isSendingEmail = true;
    this.http.post("api/board/notifyDriverAssigned", {
      bcc: this.emailBcc || null,
      subject: this.emailSubject,
      body: this.emailBody,
    }).subscribe({
      next: () => {
        this.isSendingEmail = false;
        this.toastr.success("Notification sent");
        this.closeEmailCompose();
      },
      error: () => {
        this.isSendingEmail = false;
        this.toastr.error("Failed to send notification");
      },
    });
  }

  closeEmailCompose(): void {
    this.showEmailCompose = false;
  }
}
